import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../../components/ui/Card';
import { salesApiService } from '../../api/salesApi';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from '@tanstack/react-table';
import { GlobeAltIcon, MapPinIcon, CalendarIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';

const MONTH_LABELS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const YEAR_COLORS = [
    'text-blue-600 dark:text-blue-400',
    'text-orange-600 dark:text-orange-400',
    'text-purple-600 dark:text-purple-400',
    'text-emerald-600 dark:text-emerald-400',
    'text-rose-600 dark:text-rose-400',
    'text-indigo-600 dark:text-indigo-400'
];

const normalizeCityName = (s) => (s || '').trim().toLowerCase();

function parseTripDate(val) {
    if (!val) return null;
    const s = String(val).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
}

function tripDaysFromExpense(exp) {
    const d = parseTripDate(exp.d_date);
    const r = parseTripDate(exp.r_date);
    if (d && r) {
        const diff = Math.round((r.getTime() - d.getTime()) / 86400000);
        return Math.max(0, diff + 1);
    }
    const stay = Number(exp.stay_hotel);
    return Number.isFinite(stay) && stay > 0 ? stay : 0;
}

const ComparativeVisitReport = () => {
    const [selectedYears, setSelectedYears] = useState([new Date().getFullYear(), new Date().getFullYear() - 1]);
    const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);

    const [markets, setMarkets] = useState([]);
    const [allCountries, setAllCountries] = useState([]);
    const [selectedMarketId, setSelectedMarketId] = useState(null);
    const [selectedCountryId, setSelectedCountryId] = useState(null);
    const [filteredCountries, setFilteredCountries] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [travelExpenses, setTravelExpenses] = useState([]);

    /** null = all cities; non-null array = only those ids */
    const [selectedCityIds, setSelectedCityIds] = useState(null);
    /** null = all months; non-null array = only those month numbers */
    const [selectedMonthNums, setSelectedMonthNums] = useState(null);

    const [reportsByYear, setReportsByYear] = useState({});
    const [loading, setLoading] = useState(false);

    const currentYear = new Date().getFullYear();
    const availableYears = Array.from({ length: 11 }, (_, i) => currentYear + 1 - i);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isYearDropdownOpen && !event.target.closest('.year-lookup-container')) {
                setIsYearDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isYearDropdownOpen]);

    useEffect(() => {
        const loadInitial = async () => {
            setLoading(true);
            try {
                const [mRes, cRes] = await Promise.all([
                    salesApiService.getMarketingMarkets(),
                    salesApiService.getMarketingCountries()
                ]);
                setMarkets(mRes);
                setAllCountries(cRes);
                if (mRes.length > 0) {
                    setSelectedMarketId(mRes[0].id);
                }
            } catch (e) {
                console.error('Comparative Report Init Error', e);
            } finally {
                setLoading(false);
            }
        };
        loadInitial();
    }, []);

    useEffect(() => {
        if (!selectedMarketId) return;
        const filtered = allCountries.filter(c => c.market === selectedMarketId);
        setFilteredCountries(filtered);
        if (filtered.length > 0 && !filtered.find(c => c.id === selectedCountryId)) {
            setSelectedCountryId(filtered[0].id);
        }
    }, [selectedMarketId, allCountries, selectedCountryId]);

    useEffect(() => {
        setSelectedCityIds(null);
        setSelectedMonthNums(null);
    }, [selectedCountryId]);

    useEffect(() => {
        const loadData = async () => {
            if (!selectedCountryId || selectedYears.length === 0) return;
            setLoading(true);
            try {
                const [citiesRes, expensesRes, ...reportResults] = await Promise.all([
                    salesApiService.getMarketingCities(selectedCountryId),
                    salesApiService.getTravelExpenses(),
                    ...selectedYears.map(year =>
                        salesApiService.getVisitReports({ visit_year: year, country: selectedCountryId })
                    )
                ]);

                setFilteredCities(Array.isArray(citiesRes) ? citiesRes : (citiesRes?.results || []));

                const expList = Array.isArray(expensesRes) ? expensesRes : (expensesRes?.results || expensesRes?.data || []);
                setTravelExpenses(Array.isArray(expList) ? expList : []);

                const newData = {};
                selectedYears.forEach((year, index) => {
                    const res = reportResults[index];
                    newData[year] = Array.isArray(res) ? res : (res?.results || []);
                });

                setReportsByYear(newData);
            } catch (e) {
                console.error('Comparative Data Load Error', e);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [selectedCountryId, selectedYears]);

    const citiesForTable = useMemo(() => {
        if (selectedCityIds == null) return filteredCities;
        return filteredCities.filter(c => selectedCityIds.includes(c.id));
    }, [filteredCities, selectedCityIds]);

    const monthsToIterate = useMemo(() => {
        const all = MONTH_LABELS.map((name, i) => ({ name, num: i + 1 }));
        if (selectedMonthNums == null || selectedMonthNums.length === 0) return all;
        return all.filter(m => selectedMonthNums.includes(m.num));
    }, [selectedMonthNums]);

    const daysByCityIdYear = useMemo(() => {
        const map = {};
        const nameToId = {};
        filteredCities.forEach(c => {
            nameToId[normalizeCityName(c.city_name)] = c.id;
        });

        const yearSet = new Set(selectedYears);

        travelExpenses.forEach((exp) => {
            const cityId = nameToId[normalizeCityName(exp.city)];
            if (!cityId) return;
            const dep = parseTripDate(exp.d_date);
            if (!dep) return;
            const y = dep.getFullYear();
            if (!yearSet.has(y)) return;
            const days = tripDaysFromExpense(exp);
            if (!map[cityId]) map[cityId] = {};
            map[cityId][y] = (map[cityId][y] || 0) + days;
        });

        return map;
    }, [travelExpenses, filteredCities, selectedYears]);

    const toggleYear = (year) => {
        setSelectedYears(prev => {
            if (prev.includes(year)) {
                return prev.filter(yr => yr !== year);
            }
            return [...prev, year].sort((a, b) => b - a);
        });
    };

    const toggleCityFilter = (cityId) => {
        setSelectedCityIds((prev) => {
            if (prev == null) {
                const ids = filteredCities.map(c => c.id).filter(id => id !== cityId);
                return ids.length === 0 ? [] : ids;
            }
            if (prev.includes(cityId)) {
                const next = prev.filter(id => id !== cityId);
                return next.length === 0 ? [] : next;
            }
            const next = [...prev, cityId];
            if (filteredCities.length && next.length === filteredCities.length) return null;
            return next.sort((a, b) => a - b);
        });
    };

    const toggleMonthFilter = (monthNum) => {
        setSelectedMonthNums((prev) => {
            const allNums = MONTH_LABELS.map((_, i) => i + 1);
            if (prev == null) {
                const nums = allNums.filter(m => m !== monthNum);
                return nums;
            }
            if (prev.includes(monthNum)) {
                const next = prev.filter(m => m !== monthNum);
                return next.length === 0 ? [] : next;
            }
            const next = [...prev, monthNum].sort((a, b) => a - b);
            if (next.length === 12) return null;
            return next;
        });
    };

    const clearCityFilter = () => setSelectedCityIds(null);
    const clearMonthFilter = () => setSelectedMonthNums(null);

    const columns = useMemo(() => {
        const cols = [
            {
                id: 'city_group',
                header: () => null,
                columns: [
                    {
                        accessorKey: 'city_name',
                        header: () => <span className="font-bold">City</span>,
                        cell: info => <span className="font-medium whitespace-nowrap">{info.getValue()}</span>,
                        size: 150,
                    }
                ]
            }
        ];

        monthsToIterate.forEach(({ name: monthName, num: monthNum }) => {
            const subCols = selectedYears.map((year, yIdx) => ({
                id: `m${monthNum}_y${year}`,
                header: () => (
                    <div className={`text-[9px] font-bold ${YEAR_COLORS[yIdx % YEAR_COLORS.length]}`}>
                        {year}
                    </div>
                ),
                size: 45,
                cell: (info) => {
                    const cityId = info.row.original.id;
                    const reports = reportsByYear[year] || [];
                    const r = reports.find(rd => String(rd.city) === String(cityId) && Number(rd.visit_month) === monthNum);
                    const val = r?.visit_count || 0;
                    return (
                        <div className={`text-center text-sm font-semibold ${val > 0 ? YEAR_COLORS[yIdx % YEAR_COLORS.length] : 'text-gray-200 dark:text-gray-700'}`}>
                            {val}
                        </div>
                    );
                }
            }));

            if (selectedYears.length === 2) {
                subCols.push({
                    id: `m${monthNum}_diff`,
                    header: () => <div className="text-[9px] text-gray-400">+/-</div>,
                    size: 40,
                    cell: (info) => {
                        const cityId = info.row.original.id;
                        const y1 = selectedYears[0];
                        const y2 = selectedYears[1];
                        const r1 = (reportsByYear[y1] || []).find(rd => String(rd.city) === String(cityId) && Number(rd.visit_month) === monthNum)?.visit_count || 0;
                        const r2 = (reportsByYear[y2] || []).find(rd => String(rd.city) === String(cityId) && Number(rd.visit_month) === monthNum)?.visit_count || 0;
                        const diff = r1 - r2;
                        if (diff === 0) return <div className="text-center text-[10px] text-gray-300">-</div>;
                        return (
                            <div className={`text-center text-[11px] font-bold ${diff > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {diff > 0 ? `+${diff}` : diff}
                            </div>
                        );
                    }
                });
            }

            cols.push({
                id: `month_${monthNum}`,
                header: () => (
                    <div className="text-center border-b border-gray-100 dark:border-gray-700 pb-1">
                        <span className="text-[10px] uppercase font-bold text-gray-500">{monthName}</span>
                    </div>
                ),
                columns: subCols
            });
        });

        cols.push({
            id: 'total_group',
            header: () => <div className="text-center font-bold text-gray-700 dark:text-gray-300">Total</div>,
            columns: selectedYears.map((year, yIdx) => ({
                id: `total_y${year}`,
                header: () => <div className={`text-[10px] font-bold ${YEAR_COLORS[yIdx % YEAR_COLORS.length]}`}>{year}</div>,
                size: 60,
                cell: (info) => {
                    const cityId = info.row.original.id;
                    const reports = reportsByYear[year] || [];
                    const total = reports
                        .filter(rd => String(rd.city) === String(cityId) && monthsToIterate.some(({ num }) => Number(rd.visit_month) === num))
                        .reduce((sum, r) => sum + (r.visit_count || 0), 0);
                    return (
                        <div className={`text-center font-bold py-1 ${total > 0 ? YEAR_COLORS[yIdx % YEAR_COLORS.length] : 'text-gray-200 dark:text-gray-700'}`}>
                            {total}
                        </div>
                    );
                }
            }))
        });

        return cols;
    }, [reportsByYear, selectedYears, monthsToIterate]);

    const table = useReactTable({
        data: citiesForTable,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const panelCities = citiesForTable;

    return (
        <div className="space-y-6">
            <div className="px-6 pt-4 space-y-6">
                <div className="flex flex-col gap-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Comparative Visit Report</h1>
                        <p className="text-sm text-gray-500 font-medium">Sales call comparison between selected years</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-[100rem]">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Market</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors group-focus-within:text-blue-500 text-gray-400">
                                    <GlobeAltIcon className="h-4 w-4" />
                                </div>
                                <select
                                    value={selectedMarketId || ''}
                                    onChange={(e) => setSelectedMarketId(Number(e.target.value))}
                                    className="block w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:text-white transition-all appearance-none cursor-pointer shadow-sm hover:border-gray-300 dark:hover:border-gray-600"
                                >
                                    <option value="" disabled>Select Market</option>
                                    {markets.map(m => (
                                        <option key={m.id} value={m.id}>{m.budget_market_name}</option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Country</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors group-focus-within:text-blue-500 text-gray-400">
                                    <MapPinIcon className="h-4 w-4" />
                                </div>
                                <select
                                    value={selectedCountryId || ''}
                                    onChange={(e) => setSelectedCountryId(Number(e.target.value))}
                                    className="block w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:text-white transition-all appearance-none cursor-pointer shadow-sm hover:border-gray-300 dark:hover:border-gray-600"
                                    disabled={!selectedMarketId}
                                >
                                    <option value="" disabled>{selectedMarketId ? 'Select Country' : 'First select a market'}</option>
                                    {filteredCountries.map(c => (
                                        <option key={c.id} value={c.id}>{c.country_name}</option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5 year-lookup-container">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Years to Compare</label>
                            <div className="relative group">
                                <button
                                    type="button"
                                    onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                                    className="flex items-center w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:text-white transition-all shadow-sm hover:border-gray-300 dark:hover:border-gray-600 text-left relative"
                                >
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors group-focus-within:text-blue-500 text-gray-400">
                                        <CalendarIcon className="h-4 w-4" />
                                    </div>
                                    <span className="font-bold text-blue-600 dark:text-blue-400">
                                        {selectedYears.length > 0
                                            ? `${selectedYears.length} Years Selected`
                                            : 'Select Years'}
                                    </span>
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </div>
                                </button>

                                {isYearDropdownOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-[60] py-2 max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                                        {availableYears.map(y => {
                                            const isSelected = selectedYears.includes(y);
                                            return (
                                                <button
                                                    type="button"
                                                    key={y}
                                                    onClick={() => toggleYear(y)}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors font-medium flex items-center justify-between ${isSelected
                                                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                                        : 'hover:bg-gray-50 dark:hover:bg-blue-900/10 text-gray-700 dark:text-gray-200'
                                                        }`}
                                                >
                                                    {y}
                                                    {isSelected && (
                                                        <span className="text-blue-500">
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5 xl:col-span-1">
                            <div className="flex items-center justify-between px-1">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Cities</label>
                                <button type="button" onClick={clearCityFilter} className="text-[10px] text-blue-600 hover:underline">All</button>
                            </div>
                            <div className="max-h-36 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 space-y-1 shadow-sm">
                                {filteredCities.length === 0 ? (
                                    <p className="text-xs text-gray-400 px-1">No cities</p>
                                ) : (
                                    filteredCities.map((c) => (
                                        <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded px-1 py-0.5">
                                            <input
                                                type="checkbox"
                                                checked={selectedCityIds == null || selectedCityIds.includes(c.id)}
                                                onChange={() => toggleCityFilter(c.id)}
                                                className="rounded border-gray-300"
                                            />
                                            <span className="truncate">{c.city_name}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between px-1">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Months</label>
                                <button type="button" onClick={clearMonthFilter} className="text-[10px] text-blue-600 hover:underline">All</button>
                            </div>
                            <div className="max-h-36 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 shadow-sm grid grid-cols-2 gap-1">
                                {MONTH_LABELS.map((label, i) => {
                                    const num = i + 1;
                                    const checked = selectedMonthNums == null || selectedMonthNums.includes(num);
                                    return (
                                        <label key={num} className="flex items-center gap-1.5 text-[10px] cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleMonthFilter(num)}
                                                className="rounded border-gray-300"
                                            />
                                            <span>{label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 pb-8 flex flex-col xl:flex-row gap-4 items-start">
                <Card className="shadow-xl border-0 overflow-hidden bg-white dark:bg-gray-800 flex-1 min-w-0 w-full">
                    <div className="overflow-auto max-h-[calc(100vh-320px)] scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 bg-white dark:bg-gray-800">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                {table.getHeaderGroups().map((headerGroup, idx) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                className={`
                                                    p-2 text-xs font-bold border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky z-20
                                                    ${idx === 0 ? 'top-0' : 'top-[33px]'}
                                                    ${(header.column.id === 'city_name' || header.column.id === 'city_group') ? 'left-0 z-30 shadow-[1px_0_0_0_rgba(0,0,0,0.1)]' : ''}
                                                    ${header.column.id.includes('total') ? 'bg-gray-100 dark:bg-gray-800/80' : ''}
                                                `}
                                                style={{ width: header.getSize() }}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={100} className="py-20 text-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                                        </td>
                                    </tr>
                                ) : citiesForTable.length === 0 ? (
                                    <tr>
                                        <td colSpan={100} className="py-20 text-center text-gray-400">
                                            Kayıt bulunamadı.
                                        </td>
                                    </tr>
                                ) : (
                                    table.getRowModel().rows.map(row => (
                                        <tr key={row.id} className="hover:bg-blue-50/30 dark:hover:bg-blue-900/5 group transition-colors">
                                            {row.getVisibleCells().map(cell => (
                                                <td
                                                    key={cell.id}
                                                    className={`
                                                        p-2 text-xs border-b border-dashed border-gray-100 dark:border-gray-700
                                                        ${cell.column.id === 'city_name' ? 'sticky left-0 bg-white dark:bg-gray-800 z-10 font-bold group-hover:bg-blue-50/50 dark:group-hover:bg-gray-800/50 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]' : ''}
                                                        ${cell.column.id.includes('diff') ? 'bg-gray-50/30 dark:bg-gray-900/10' : ''}
                                                        ${cell.column.id.includes('total') ? 'bg-gray-50/50 dark:bg-gray-900/40 font-bold' : ''}
                                                    `}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>

                <aside className="w-full xl:w-72 shrink-0">
                    <Card className="shadow-xl border-0 overflow-hidden bg-white dark:bg-gray-800 p-4">
                        <div className="flex items-center gap-2 mb-3 text-gray-800 dark:text-gray-100">
                            <BuildingOffice2Icon className="w-5 h-5 text-blue-500" />
                            <h2 className="text-sm font-bold">Travel days (expenses)</h2>
                        </div>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-3">
                            Totals by city from travel expense trip length (dep–arr) or stay nights, matched by city name.
                        </p>
                        <div className="overflow-x-auto max-h-[calc(100vh-360px)] scrollbar-thin">
                            <table className="w-full text-left text-xs">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-600">
                                        <th className="py-2 pr-2 font-semibold text-gray-600 dark:text-gray-300">City</th>
                                        {selectedYears.map((y) => (
                                            <th key={y} className="py-2 px-1 text-center font-semibold text-gray-600 dark:text-gray-300">{y}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {panelCities.length === 0 ? (
                                        <tr>
                                            <td colSpan={selectedYears.length + 1} className="py-6 text-center text-gray-400">No rows</td>
                                        </tr>
                                    ) : (
                                        panelCities.map((city) => (
                                            <tr key={city.id} className="border-b border-gray-100 dark:border-gray-700">
                                                <td className="py-2 pr-2 font-medium text-gray-800 dark:text-gray-200 truncate max-w-[8rem]" title={city.city_name}>
                                                    {city.city_name}
                                                </td>
                                                {selectedYears.map((y) => (
                                                    <td key={`${city.id}-${y}`} className="py-2 px-1 text-center tabular-nums text-gray-700 dark:text-gray-300">
                                                        {daysByCityIdYear[city.id]?.[y] ?? 0}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </aside>
            </div>
        </div>
    );
};

export default ComparativeVisitReport;
